import { finalize } from 'rxjs';
import { Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PropertyService } from '../../../_services/properties/property.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastService } from '../../../_services/toast.service';

interface MediaItem {
  url: string;
  type: 'gallery' | 'video' | 'plan';
}

@Component({
  selector: 'app-image-upload',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './image-upload.component.html',
})
export class ImageUploadComponent implements OnInit {

  @ViewChild('fileInput') fileInput!: ElementRef;
  uploadType: 'gallery' | 'plan'|'video' = 'gallery';
  selectedFiles: File[] = [];
  previews: MediaItem[] = [];
  isDragging = false;
  propertyId: string | null = null;
  showUploadSuccessModal = false;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private readonly _PS: PropertyService,
    private ngZone: NgZone,
    private sanitizer: DomSanitizer,
    private _NS: ToastService
  ) {}

  ngOnInit(): void {
    this.propertyId = this.route.snapshot.paramMap.get('id');

    // 2. Get Mode from the Query (?mode=...)
    this.route.queryParamMap.subscribe((params) => {
      const mode = params.get('mode');
      if (mode === 'gallery' || mode === 'plan') {
        this.uploadType = mode;
      } else{
        this.uploadType = 'video';
      }
    });
  }

  // media center
  setUploadType(type: 'gallery' | 'plan' | 'video') {
    if (this.isUploading) return;
    this.uploadType = type;
    this.clearAll();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.processFiles(input.files);
    input.value = ''; // allow re-select same file
  }

  onFileDropped(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(files);
      if (this.fileInput) this.fileInput.nativeElement.value = '';
    }
  }

  processFiles(files: FileList) {
    if (this.isUploading) return;
    const filesArray = Array.from(files);
    // Remove window.location.origin. Relative paths are more reliable in Angular.
    const logoSrc = 'assets/logo_watermark.png';

    filesArray.forEach((file) => {
      // 1. Handle Videos
      if (file.type.startsWith('video/')) {
        this.handleVideoFile(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) return;

          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);

          // --- 1. Load and Draw Logo ---
          try {
            const logo = await this.loadLogo(logoSrc);
            const logoWidth = canvas.width * 0.35;
            const logoHeight = (logo.height / logo.width) * logoWidth;
            const xPos = canvas.width / 2 - logoWidth / 2;
            const yPos = canvas.height / 2 - logoHeight / 2;
            ctx.save();
            ctx.globalAlpha = 0.9;
            ctx.drawImage(logo, xPos, yPos, logoWidth, logoHeight);
            ctx.restore();
          } catch (error) {
            // If the logo fails, we log it but continue so the user's image isn't lost
            console.error('Logo path invalid or file missing at:', logoSrc);
          }

          // --- 2. Draw Text Watermark ---
          const watermarkText = '';
          if (watermarkText) {
            const fontSize = Math.floor(canvas.width * 0.03);
            ctx.save();
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(-Math.PI / 4);
            ctx.font = `bold ${fontSize}px sans-serif`;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.textAlign = 'center';

            const spacingX = fontSize * 5;
            const spacingY = fontSize * 3;
            for (let x = -canvas.width; x < canvas.width; x += spacingX) {
              for (let y = -canvas.height; y < canvas.height; y += spacingY) {
                ctx.fillText(watermarkText, x, y);
              }
            }
            ctx.restore();
          }

          this.finalizeCanvas(canvas, file);
        };
      };
      reader.readAsDataURL(file);
    });
  }

  private loadLogo(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const logo = new Image();
      // Setting crossOrigin before src helps avoid canvas tainting
      logo.crossOrigin = 'anonymous';
      logo.onload = () => resolve(logo);
      logo.onerror = () => reject(new Error(`Could not load image at ${src}`));
      logo.src = src;
    });
  }

  // Helper to handle the output to avoid code duplication
  private finalizeCanvas(canvas: HTMLCanvasElement, file: File) {
    const previewUrl = canvas.toDataURL(file.type);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const watermarkedFile = new File([blob], file.name, { type: file.type });
      this.ngZone.run(() => {
        if (this.uploadType === 'plan') {
          this.selectedFiles = [...this.selectedFiles,watermarkedFile];
          this.previews.push({url:previewUrl,type:'plan'})
          // this.previews = [previewUrl];
        } else if (this.uploadType === 'gallery') {
          this.selectedFiles = [...this.selectedFiles,watermarkedFile];
          this.previews.push({url:previewUrl,type:'gallery'})
          // this.previews = [previewUrl];
        } else {
          this.previews.push({url:previewUrl,type:'video'})
          this.selectedFiles = [...this.selectedFiles, watermarkedFile];
          // this.previews = [...this.previews, previewUrl];
        }
      });
    }, file.type);
  }

  private handleVideoFile(file: File) {
    const videoUrl = URL.createObjectURL(file);
    this.ngZone.run(() => this.updateState(file, videoUrl));
  }

  private updateState(file: File, preview: string) {
    if (this.uploadType === 'plan') {
      this.selectedFiles = [file];
      this.previews.push({url:preview,type:'plan'})

    } else if (this.uploadType === 'gallery') {
      this.selectedFiles = [file];
      this.previews.push({url:preview,type:'gallery'})
      // this.previews = [preview];
    }else {
      this.selectedFiles = [...this.selectedFiles, file];
      this.previews.push({url:preview,type:'video'})      
      // this.previews = [...this.previews, preview];
    }
  }

  removeImage(index: number) {
    this.selectedFiles.splice(index, 1);
    this.previews.splice(index, 1);
  }

  clearAll() {
    this.selectedFiles = [];
    // this.previews = [];
  }

  isUploading = false;

  uploadImages() {
    if (this.isUploading || this.selectedFiles.length === 0) return;
    this.isUploading = true;
    const formData = new FormData();
    this.selectedFiles.forEach((file: File) => {
      formData.append('files', file, file.name);
    });
    let imgData = {
      id: this.propertyId,
      type: this.uploadType,
      files: formData,
    };
    this._PS.addPropertyImg(imgData).pipe(
      finalize(() => { this.isUploading = false; }),
    ).subscribe({
      next: (res) => {
        if (res.status) {
          this.uploadType = this.uploadType; // Store for the modal text
        this.showUploadSuccessModal = true;
        this.clearAll(); // Clear current selection/previews
        } else {
          this._NS.error(res.message)
        }
      },
      error: (err) => this._NS.error(err),
    });
  }

  uploadMore() {
    this.showUploadSuccessModal = false;
    // User stays on the same page to upload more
  }

  goToList() {
    this.router.navigate(['admin/property']); // Adjust to your route path
  }
  propertyDetails(propertyId: any) {
    this.router.navigate(['admin/property/details'], {
      queryParams: {
        id: propertyId,
        mode: 'details',
      },
    });
  }
}
